import { useCallback, useState, useEffect } from 'react';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import dynamic from 'next/dynamic';
import draftToHtml from 'draftjs-to-html';

// import { Editor } from 'react-draft-wysiwyg';
const Editor = dynamic(() => import('react-draft-wysiwyg').then((mod) => mod.Editor), {
	ssr: false
});

// import htmlToDraft from 'html-to-draftjs';
const htmlToDraft = typeof window === 'object' && require('html-to-draftjs').default;

import {
	toolbar,
	editorStyleNormal,
	editorStyleError,
	editorStyleFocused,
	toolbarStyleNormal,
	toolbarStyleError,
	toolbarStyleFocused
} from './RichTextEditor.config';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import classes from './RichTextEditor.module.scss';
import ActionButton from '../actionbutton/ActionButton';

const RichTextEditor = ({
	data,
	resultType = 'html',
	isHtml,
	dataEntryType = 'html',
	id = 'richtext-editor',
	name = 'richtext-editor',
	placeholder,
	label,
	onChange,
	isRequired,
	setValidator,
	horizental = true,
	readMore = false
}) => {
	const [isFocused, setIsFocused] = useState(false);
	const [isEmpty, setIsEmpty] = useState(data ? false : true);
	const [shouldValidate, setShouldValidate] = useState(false);

	const [editorState, setEditorState] = useState(() => {
		if (!data) {
			return EditorState.createEmpty();
		} else if (data && dataEntryType === 'html') {
			const contentBlock = htmlToDraft(data);
			if (contentBlock) {
				const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
				const editorState = EditorState.createWithContent(contentState);
				return editorState;
			} else {
				return EditorState.createEmpty();
			}
		} else if (data && dataEntryType === 'json') {
			const contentState = ContentState.createFromBlockArray(data);
			const editorState = EditorState.createWithContent(contentState);
			return editorState;
		}
	});

	const onEditorStateChange = (newEditorState) => {
		setIsEmpty(() => {
			const isempty = convertToRaw(newEditorState.getCurrentContent()).blocks.every(
				(b) => b.text.trim() === ''
			);
			return isempty;
		});
		setEditorState(newEditorState);
		if (resultType === 'html') {
			onChange && onChange(draftToHtml(convertToRaw(newEditorState.getCurrentContent())));
		} else if (resultType === 'json') {
			onChange && onChange(convertToRaw(newEditorState.getCurrentContent()));
		}
	};

	const editorStyle = useCallback(() => {
		if (isRequired && isEmpty && shouldValidate) {
			return editorStyleError;
		} else if (isFocused) {
			return editorStyleFocused;
		} else {
			return editorStyleNormal;
		}
	}, [isRequired, isEmpty, isFocused, shouldValidate]);

	const toolbarStyle = useCallback(() => {
		if (isRequired && isEmpty && shouldValidate) {
			return toolbarStyleError;
		} else if (isFocused) {
			return toolbarStyleFocused;
		} else {
			return toolbarStyleNormal;
		}
	}, [isRequired, isEmpty, isFocused, shouldValidate]);

	const validationHandler = () => {
		setShouldValidate(true);
		return !isEmpty;
	};

	useEffect(() => {
		setValidator && setValidator(name, validationHandler);
	}, [isEmpty]);

	if (!isHtml) {
		return (
			<div className={`d-flex row ${horizental ? 'flex-row' : 'flex-column'} ${classes['rich-text-editor-wrapper']}`}>
				<label htmlFor={id} name={name} className="col-12 col-xl-4">
					{label}
				</label>
				<div className="col-12 ">
					<Editor
						toolbar={toolbar}
						id={id}
						name={name}
						editorState={editorState}
						onEditorStateChange={onEditorStateChange}
						placeholder={placeholder}
						editorStyle={editorStyle()}
						toolbarStyle={toolbarStyle()}
						onFocus={() => setIsFocused(true)}
						onBlur={() => setIsFocused(false)}
						stripPastedStyles={true}
					/>
				</div>
			</div>
		);
	} else {
		const [showAll, setShowAll] = useState(false);
		const htmlContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
		const maxLines = 20;

		// Inline style for limiting lines
		const limitedStyle = {
			display: '-webkit-box',
			WebkitLineClamp: maxLines,
			WebkitBoxOrient: 'vertical',
			overflow: 'hidden'
		};

		// Count the number of lines in the htmlContent
		// Count the number of lines in the htmlContent by splitting on block-level tags
		const lineCount = htmlContent
			.replace(/<\/(p|div|li|h[1-6]|ul|ol|blockquote)>/gi, '\n')
			.replace(/<br\s*\/?>/gi, '\n')
			.split('\n')
			.filter(line => line.trim() !== '').length;

		return (
			<div className={classes['rich-text-html']}>
				<div
					style={(readMore && !showAll && lineCount > maxLines) ? limitedStyle : undefined}
					dangerouslySetInnerHTML={{
						__html: htmlContent
					}}
				/>
				{readMore && lineCount > maxLines && (
					<ActionButton
						kind='blank'
						className={classes['show-more-btn']}
						onClick={() => setShowAll((prev) => !prev)}
					>
						{showAll ? 'Show Less' : 'Show More'}
					</ActionButton>
				)}
			</div>
		);
	}
};

export default RichTextEditor;
